import SEO from "@/components/SEO";

const PUBLISHER = {
  name: "OptimHub",
  legal_form: "SAS (Société par Actions Simplifiée)",
  capital: "5 001 €",
  siren: "534 057 088",
  siret: "534 057 088 00039",
  vat: "FR63534057088",
  rcs: "Paris",
  rcs_date: "11 août 2011",
  naf: "7021Z — Conseil en relations publiques et communication",
  address: "11 Rue de Lourmel, 75015 Paris, France",
  publication_director: "Stéphane Dilouya",
  contact_email: "contact@choose-my-hotel.com",
};

const SECTIONS = [
  { id: "publisher", label: "Publisher" },
  { id: "hosting", label: "Hosting" },
  { id: "ip", label: "Intellectual property" },
  { id: "affiliate", label: "Affiliate disclosure" },
  { id: "privacy", label: "Privacy & cookies" },
  { id: "terms", label: "Terms of use" },
  { id: "liability", label: "Liability" },
  { id: "law", label: "Governing law" },
];

export default function LegalPage() {
  return (
    <div className="bg-sand">
      <SEO title="Legal notice" description="Legal information, publisher details, privacy policy, terms of use, and affiliate disclosure for Choose My Hotel." />

      {/* HERO */}
      <section className="bg-charcoal text-sand relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl bg-terracotta/30 pointer-events-none" />
        <div className="relative container-x py-20 md:py-24 max-w-4xl">
          <div className="overline text-terracotta-light">Legal</div>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Legal notice & policies.
          </h1>
          <p className="mt-6 text-sand/80 text-lg leading-relaxed max-w-2xl">
            Publisher information, intellectual property, affiliate disclosure, privacy & cookies, and terms of use.
          </p>
          <div className="mt-10 flex flex-wrap gap-2">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`legal-toc-${s.id}`}
                className="text-[10px] uppercase tracking-[0.18em] border border-sand/25 text-sand/85 hover:bg-sand hover:text-charcoal px-3 py-1.5 transition-colors"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container-x py-16 md:py-20 max-w-4xl space-y-14 md:space-y-16">
        <Block id="publisher" num="01" title="Site publisher">
          <p>
            This website (<em>choose-my-hotel.com</em>) is published by <strong>{PUBLISHER.name}</strong>, {PUBLISHER.legal_form}, with a share capital of {PUBLISHER.capital}.
          </p>
          <dl className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm">
            <Row k="Registered office" v={PUBLISHER.address} />
            <Row k="SIREN" v={PUBLISHER.siren} />
            <Row k="SIRET (head office)" v={PUBLISHER.siret} />
            <Row k="VAT number" v={PUBLISHER.vat} />
            <Row k="RCS" v={`${PUBLISHER.rcs} — registered ${PUBLISHER.rcs_date}`} />
            <Row k="NAF / APE" v={PUBLISHER.naf} />
            <Row k="Director of publication" v={PUBLISHER.publication_director} />
            <Row k="Contact" v={<a href={`mailto:${PUBLISHER.contact_email}`} className="text-terracotta hover:underline">{PUBLISHER.contact_email}</a>} />
          </dl>
        </Block>

        <Block id="hosting" num="02" title="Hosting">
          <p>
            The website is hosted on Emergent's managed cloud infrastructure. Static assets are delivered through a Cloudflare CDN edge network. The publisher can provide the hosting provider's full contact details on written request to {PUBLISHER.contact_email}.
          </p>
        </Block>

        <Block id="ip" num="03" title="Intellectual property">
          <p>
            All content on this website — editorial reviews, scoring methodology, illustrations, layouts, code and trademarks — is the exclusive property of {PUBLISHER.name} unless otherwise stated. Operator names, logos and brand colors remain the property of their respective owners and are used here strictly for editorial reference under fair use.
          </p>
          <p>
            Reproduction, redistribution or commercial reuse of any material from this site without prior written consent is prohibited.
          </p>
        </Block>

        <Block id="affiliate" num="04" title="Affiliate disclosure">
          <p>
            Choose My Hotel is an independent editorial guide funded in part by affiliate partnerships. When you click an outbound link to a hotel operator (Booking.com, Hotels.com, Choice Hotels, Marriott, Hilton, Hyatt, IHG, etc.) and complete a booking, we may earn a commission at no extra cost to you.
          </p>
          <p>
            <strong>Affiliate revenue never influences our scores or rankings.</strong> Editorial scoring is governed exclusively by our published five-criteria methodology. No operator pays for placement or coverage. Outbound affiliate links are clearly identifiable through the <em>rel="sponsored"</em> attribute and traffic-tracking subdomains (e.g. click.beyondcheap.com).
          </p>
        </Block>

        <Block id="privacy" num="05" title="Privacy & cookies">
          <p>
            We collect a minimal amount of personal data, exclusively to operate the service:
          </p>
          <ul className="list-disc pl-5 mt-3 space-y-1.5">
            <li><strong>Newsletter subscription</strong> — we store your email address solely to deliver the weekly digest. You can unsubscribe at any time via the link in every email.</li>
            <li><strong>Affiliate clicks</strong> — when you click an outbound link to a partner, we log the operator name, click timestamp and a non-identifying user-agent fragment to validate commissions and detect fraud. This data does not include personal identifiers.</li>
            <li><strong>Anonymous analytics</strong> — we use privacy-respectful product analytics (PostHog) to count page views and improve the editorial.</li>
          </ul>
          <p className="mt-4">
            We do not sell, rent or share personal data with third parties. Under GDPR, you have the right to access, rectify, port and delete your data — write to {PUBLISHER.contact_email}.
          </p>
          <p>
            Cookies are limited to: a session cookie for the admin dashboard, a Cloudflare bot-management cookie (security), and a PostHog analytics cookie (anonymous). Strictly functional cookies do not require consent.
          </p>
        </Block>

        <Block id="terms" num="06" title="Terms of use">
          <p>
            By accessing this website you agree to use it for lawful, personal informational purposes only. Automated scraping, mass downloading or attempts to compromise the security of the service are prohibited. Editorial content is provided "as is" — while we work hard to keep operator information accurate, hotel inventories, prices and loyalty programs change frequently, and we recommend you confirm every detail directly with the operator before booking.
          </p>
        </Block>

        <Block id="liability" num="07" title="Liability">
          <p>
            {PUBLISHER.name} acts as an editorial publisher and is not a travel agent. We do not handle payments, reservations, refunds or customer service for hotel stays — these are the sole responsibility of the operator (Booking.com, Marriott, Hilton, etc.) you choose to book through.
          </p>
          <p>
            Editorial scores reflect the opinion of our editors at the time of publication, based on the methodology disclosed on this site. They do not constitute financial or legal advice.
          </p>
        </Block>

        <Block id="law" num="08" title="Governing law">
          <p>
            This notice is governed by French law. Any dispute relating to the website that cannot be resolved amicably will be brought before the competent courts of {PUBLISHER.rcs}, France.
          </p>
        </Block>

        <p className="text-xs text-charcoal/50 pt-6">Last updated: February 2026.</p>
      </section>
    </div>
  );
}

function Block({ id, num, title, children }) {
  return (
    <section id={id} data-testid={`legal-section-${id}`} className="scroll-mt-28">
      <div className="flex items-center gap-4 mb-5">
        <span className="font-mono text-xs text-terracotta">{num}</span>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{title}</h2>
      </div>
      <div className="prose prose-charcoal max-w-none text-charcoal/80 leading-relaxed space-y-4">
        {children}
      </div>
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="border-t border-charcoal/10 pt-3">
      <dt className="overline text-[10px] text-charcoal/55 mb-1">{k}</dt>
      <dd className="text-charcoal">{v}</dd>
    </div>
  );
}
